import { Injectable } from '@angular/core'
import Quill from 'quill'
import Embed from 'quill/blots/embed'
import { Placeholder } from '@features/content-unit/models/placeholder.model'

// Custom Quill Blot for Placeholders. This registers a custom element with Quill.
class PlaceholderBlot extends Embed {
  public static override readonly ['blotName'] = 'placeholder'
  public static override readonly ['tagName'] = 'span'
  public static override readonly ['className'] = 'placeholder-chip'

  /**
   * Creates the DOM node for the placeholder blot.
   * @param value - The value containing id and label for the placeholder.
   * @returns The created HTML element.
   */
  public static override create(value: { id: string; label: string }): HTMLElement {
    const node = super.create(value) as HTMLElement
    node.dataset['id'] = value.id
    node.dataset['label'] = value.label
    node.setAttribute('contenteditable', 'false')
    node.innerText = value.label
    return node
  }

  /**
   * Returns the value of the placeholder blot from its DOM node.
   * @param node - The DOM node of the blot.
   * @returns The id and label of the placeholder.
   */
  public static override value(node: HTMLElement): { id: string | undefined; label: string | undefined } {
    return {
      id: node.dataset['id'],
      label: node.dataset['label']
    }
  }
}

// Register the custom blot with Quill. This only needs to be done once.
Quill.register(PlaceholderBlot)

@Injectable({
  providedIn: 'root'
})
export class QuillHelperService {
  /**
   * Converts a string of HTML containing placeholder chips into a code format (e.g., ##placeholder_id##).
   * @param html - The HTML string to convert.
   * @returns A string with placeholders in code format.
   */
  public convertToCode(html: string): string {
    const container = document.createElement('div')
    container.innerHTML = html
    const spans = container.querySelectorAll('span.placeholder-chip')
    spans.forEach(span => {
      const id = (span as HTMLElement).dataset['id']
      if (typeof id === 'string' && id !== '') {
        span.replaceWith(`##${id}##`)
      }
    })
    return container.innerHTML
  }

  /**
   * Converts a string containing placeholders in code format (e.g., ##placeholder_id##) into an HTML string with placeholder chips.
   * @param code - The string with placeholders in code format.
   * @param placeholders - An array of available placeholders to look up labels.
   * @returns An HTML string with visual placeholder chips.
   */
  public convertToVisual(code: string, placeholders: Placeholder[]): string {
    if (!code) {
      return ''
    }
    return code.replaceAll(/##([^#\s]+)##/g, (_match: string, id: string): string => {
      const placeholder = placeholders.find(p => p.placeholder === id)
      const label = placeholder?.label ?? id
      return `<span class="placeholder-chip" data-id="${id}" data-label="${label}" contenteditable="false">${label}</span>`
    })
  }
}
